import { useState } from 'react';
import { useNavigate } from 'react-router';
import { formatDate, formatTime, toBerlinDateKey, type TimelinePortal } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button, Textarea, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { DeadlineDot } from '../../tickets/ui/DeadlineDot';
import { TicketPill } from '../../tickets/ui/TicketPill';
import { addDays, todayKey } from '../format';
import { confirmAppointmentRequest, editAssistantNote, useInvalidatePortal } from '../portal-api';
import type { TimelineItem } from '../types';
import styles from './portal.module.css';

export interface PortalTimelineEntryProps {
  item: TimelineItem & { portal: TimelinePortal };
  contactId: string;
  /** Draws the vertical line down to the next entry (the next item is a counterpart as well). */
  connect: boolean;
}

const PILL_CLASS: Record<TimelinePortal['pill'], string | undefined> = {
  Termin: styles.pillTermin,
  Notiz: styles.pillNotiz,
  Portal: styles.pillPortal,
  Dokument: styles.pillDokument,
  Supportticket: styles.pillSupportticket,
};

const DOT_CLASS: Record<Exclude<TimelinePortal['pill'], 'Supportticket'>, string | undefined> = {
  Termin: styles.dotAccent,
  Notiz: undefined,
  Portal: styles.dotNavy,
  Dokument: styles.dotWarning,
};

/** „heute, 11:06" · „gestern, 11:06" · „03.09.2026, 10:06" (Portal 27/28). */
export function entryTime(iso: string): string {
  const key = toBerlinDateKey(iso);
  const today = todayKey();
  const day = key === today ? 'heute' : key === addDays(today, -1) ? 'gestern' : formatDate(iso);
  return `${day}, ${formatTime(iso)}`;
}

/**
 * Mandantenportal 27/28 — a timeline item with its CRM counterpart (`item.portal`): dot on the vertical line,
 * card with type pill, title, time, text, status pill + meta, footnote and the actions „Bestätigen",
 * „Verschieben", „Notiz bearbeiten" and „Ticket öffnen". A pending chat appointment and an open portal ticket
 * are highlighted.
 */
export function PortalTimelineEntry({ item, contactId, connect }: PortalTimelineEntryProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidate = useInvalidatePortal();
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const portal = item.portal;
  const refId = item.ref?.id ?? null;
  const can = (action: TimelinePortal['actions'][number]) => portal.actions.includes(action);
  const pending = can('confirm_appointment');
  const openTicket =
    portal.pill === 'Supportticket' &&
    (portal.statusPill?.tone === 'warning' || portal.statusPill?.tone === 'info');
  const highlight = pending || openTicket;
  const time = portal.pill === 'Termin' && portal.meta ? portal.meta : entryTime(item.occurredAt);
  const statusMeta = portal.pill === 'Termin' ? null : portal.meta;
  const compact =
    !item.text &&
    !portal.footnote &&
    !portal.statusPill &&
    !statusMeta &&
    portal.actions.length === 0 &&
    !editing;

  async function confirm() {
    if (refId === null) return;
    setConfirming(true);
    try {
      await confirmAppointmentRequest(refId);
      toast({ message: 'Termin bestätigt.' });
      await invalidate(contactId);
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setConfirming(false);
    }
  }

  async function saveNote() {
    const text = draft.trim();
    if (text === '') {
      setDraftError('Bitte geben Sie einen Text ein.');
      return;
    }
    setSaving(true);
    setDraftError(null);
    try {
      await editAssistantNote(contactId, item.id, text);
      setEditing(false);
      toast({ message: 'Notiz gespeichert.' });
      await invalidate(contactId);
    } catch (error) {
      setDraftError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  const pill = <span className={clsx(styles.pill, PILL_CLASS[portal.pill])}>{portal.pill}</span>;

  return (
    <div className={styles.entry}>
      <div className={styles.rail} aria-hidden="true">
        {portal.pill === 'Supportticket' ? (
          <DeadlineDot tone="danger" size={8} />
        ) : (
          <span className={clsx(styles.dot, DOT_CLASS[portal.pill])} />
        )}
        {connect ? <span className={styles.line} /> : null}
      </div>
      {compact ? (
        <div className={clsx(styles.card, styles.cardCompact)}>
          {pill}
          <span className={clsx(styles.title, styles.titlePlain)}>{item.title}</span>
          <span className={styles.time}>{time}</span>
        </div>
      ) : (
        <article
          className={clsx(styles.card, highlight && styles.cardHighlight)}
          aria-label={`${portal.pill}: ${item.title}`}
        >
          <div className={styles.head}>
            {pill}
            <span className={styles.title}>{item.title}</span>
            <span className={styles.time}>{time}</span>
          </div>
          {editing ? (
            <div className={styles.noteEdit}>
              <Textarea
                label="Notiz"
                labelHidden
                rows={4}
                autoFocus
                value={draft}
                error={draftError ?? undefined}
                onChange={(event) => setDraft(event.target.value)}
              />
              <div className={styles.noteEditActions}>
                <Button variant="secondary" size="small" disabled={saving} onClick={() => setEditing(false)}>
                  Abbrechen
                </Button>
                <Button size="small" loading={saving} onClick={() => void saveNote()}>
                  Speichern
                </Button>
              </div>
            </div>
          ) : item.text ? (
            portal.pill === 'Termin' ? (
              <div className={styles.lineText}>{item.text}</div>
            ) : (
              <p className={styles.text}>{item.text}</p>
            )
          ) : null}
          {can('confirm_appointment') || can('reschedule_appointment') ? (
            <div className={styles.buttons}>
              {can('confirm_appointment') ? (
                <Button size="small" loading={confirming} onClick={() => void confirm()}>
                  Bestätigen
                </Button>
              ) : null}
              {can('reschedule_appointment') && refId !== null ? (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => void navigate(`/calendar/appointments/${refId}`)}
                >
                  Verschieben
                </Button>
              ) : null}
            </div>
          ) : null}
          {portal.statusPill || statusMeta || can('open_ticket') ? (
            <div className={styles.statusRow}>
              {portal.statusPill ? (
                <TicketPill label={portal.statusPill.label} tone={portal.statusPill.tone} />
              ) : null}
              {statusMeta ? <span className={styles.meta}>{statusMeta}</span> : null}
              {can('open_ticket') && refId !== null ? (
                <button
                  type="button"
                  className={styles.link}
                  onClick={() => void navigate(`/tickets/${refId}`)}
                >
                  Ticket öffnen
                </button>
              ) : null}
            </div>
          ) : null}
          {portal.footnote || (can('edit_note') && !editing) ? (
            <div className={styles.foot}>
              {portal.footnote ? <span>{portal.footnote}</span> : null}
              {can('edit_note') && !editing ? (
                <button
                  type="button"
                  className={clsx(styles.link, styles.linkSmall)}
                  onClick={() => {
                    setDraft(item.text ?? '');
                    setDraftError(null);
                    setEditing(true);
                  }}
                >
                  Notiz bearbeiten
                </button>
              ) : null}
            </div>
          ) : null}
        </article>
      )}
    </div>
  );
}
